"use client";
/**
 * Small settings card that sits above the calculators grid.
 * Lets the user flip US/Metric and set their default email — persists via
 * CalcSettingsProvider.
 */
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { RotateCcw, Settings2 } from "lucide-react";
import { useCalcSettings } from "./settings-context";

export function CalcSettingsPanel() {
  const { settings, update, reset } = useCalcSettings();

  return (
    <section style={{ padding: "0 24px 32px", position: "relative", zIndex: 1 }}>
      <div style={{ maxWidth: 1100, margin: "0 auto", background: "rgba(13,25,41,0.88)", backdropFilter: "blur(10px)", WebkitBackdropFilter: "blur(10px)", borderRadius: 20, border: "1px solid rgba(255,255,255,0.18)", borderTop: "3px solid #B88A2F", padding: "24px 28px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16 }}>
          <Settings2 size={18} style={{ color: "#D4A84B" }} />
          <h2 style={{ fontSize: 18, fontWeight: 700, color: "#ffffff", fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Defaults</h2>
          <span style={{ fontSize: 12, color: "rgba(255,255,255,0.6)", marginLeft: "auto" }}>Applied across all calculators. Saved locally.</span>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 16 }}>
          {/* Units radio */}
          <div>
            <Label style={{ color: "rgba(255,255,255,0.82)", marginBottom: 6, display: "block" }}>Units</Label>
            <div style={{ display: "flex", gap: 6 }}>
              <button
                type="button"
                onClick={() => update({ units: "us" })}
                aria-pressed={settings.units === "us"}
                style={segmentStyle(settings.units === "us")}
              >
                US (ft, gal, °F)
              </button>
              <button
                type="button"
                onClick={() => update({ units: "metric" })}
                aria-pressed={settings.units === "metric"}
                style={segmentStyle(settings.units === "metric")}
              >
                Metric (m, L, °C)
              </button>
            </div>
          </div>

          {/* Default linear / height */}
          <div>
            <Label htmlFor="set-linear" style={{ color: "rgba(255,255,255,0.82)" }}>Default linear (ft)</Label>
            <Input id="set-linear" type="number" inputMode="numeric" value={settings.defaultLinearFt} onChange={(e) => update({ defaultLinearFt: Number(e.target.value) || 0 })} style={{ marginTop: 6 }} />
          </div>
          <div>
            <Label htmlFor="set-height" style={{ color: "rgba(255,255,255,0.82)" }}>Default ceiling height (ft)</Label>
            <Input id="set-height" type="number" inputMode="numeric" value={settings.defaultHeightFt} onChange={(e) => update({ defaultHeightFt: Number(e.target.value) || 0 })} style={{ marginTop: 6 }} />
          </div>
          <div>
            <Label htmlFor="set-email" style={{ color: "rgba(255,255,255,0.82)" }}>Default email (for Email Results)</Label>
            <Input id="set-email" type="email" value={settings.defaultEmail} onChange={(e) => update({ defaultEmail: e.target.value })} placeholder="name@company.com" style={{ marginTop: 6 }} />
          </div>
        </div>

        <div style={{ marginTop: 16 }}>
          <Button variant="ghost" onClick={reset} style={{ color: "rgba(255,255,255,0.7)" }}>
            <RotateCcw className="mr-2 size-4" /> Reset to defaults
          </Button>
        </div>
      </div>
    </section>
  );
}

function segmentStyle(active: boolean): React.CSSProperties {
  return {
    flex: 1,
    padding: "9px 12px",
    borderRadius: 8,
    border: "1px solid " + (active ? "#D4A84B" : "rgba(255,255,255,0.18)"),
    background: active ? "#B88A2F" : "transparent",
    color: active ? "#080F1A" : "rgba(255,255,255,0.85)",
    fontSize: 13,
    fontWeight: 700,
    letterSpacing: "0.02em",
    cursor: "pointer",
  };
}
